import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import FinancialsFromBanking from './components/FinancialsFromBanking/FinancialsFromBanking';
import styles from './Financials.module.scss';
import ProvisionalFinancials from './components/ProvisionalFinanicals/ProvisionalFinancials';
import AuditedFinancialsVisualisation from '../AuditedFinancialsVisualisation/AuditedFinancialsVisualisation';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Mixpanel } from '../../../../utils/mixpanel';
import { AICA_CASHFLOW_BANKING, AICA_FINANCIAL_STATEMENTTYPE_SWITCHED } from '../../../../utils/constants/mixpanelEvents/investorEvents';
import { ctaPurpleRight } from '../../../../assets/hostedassets';
import AicaOverview from '../../../../common/AicaOverview/AicaOverview';
import FinancialSummaryTab from './components/FinancialSummaryTab/FinancialSummaryTab';
import { updateQueryParams } from '../../../../utils/searchParamUtils';
import { useIsMobile } from '../../../../common/_custom/Hooks/useIsMobile';
import { KEYBOARD_EVENTS } from '../../../../utils/constants/commonConstants';
import ReconPill from '../../components/Reconciliations/components/ReconPill';
import { RECON_SCOPE } from '../../../../store/reconciliations/constants';
import { FINANCIAL_SUMMARY, AUDITED_FINANCIALS, PROVISIONAL_FINANCIALS, CASHFLOW_FROM_BANKING, FINANCIALS_TYPES } from './constants';

// Re-exported for existing importers (e.g. BankDataTable, AicaPDFReport) — the values
// themselves live in ./constants so they can be imported without pulling in this component.
export { FINANCIAL_SUMMARY, AUDITED_FINANCIALS, PROVISIONAL_FINANCIALS, CASHFLOW_FROM_BANKING };

interface FinancialsProps {
    selectedFinancials?: string;
    selectedMetric?: string;
    assesseeOrgId?: string;
    companyName?: string;
}

function Financials({ selectedFinancials = '', selectedMetric = '', assesseeOrgId = '', companyName = '' }: FinancialsProps) {
    const [selectedFinancialsType, setSelectedFinancialsType] = useState(FINANCIAL_SUMMARY);
    const [params, setParams] = useSearchParams();
    const location = useLocation();
    const isMobile = useIsMobile();

    const isPDFReport = !!(selectedFinancials && selectedMetric);

    useEffect(() => {
        if (isPDFReport && [AUDITED_FINANCIALS, PROVISIONAL_FINANCIALS, CASHFLOW_FROM_BANKING].includes(selectedFinancials)) {
            setSelectedFinancialsType(selectedFinancials);
        }
    }, [isPDFReport]);

    const getRenderComponent = () => {
        switch (selectedFinancialsType) {
            case FINANCIAL_SUMMARY:
                return <FinancialSummaryTab assesseeOrgId={assesseeOrgId} isPDFReport={isPDFReport} />;
            case CASHFLOW_FROM_BANKING:
                return <FinancialsFromBanking />;
            case AUDITED_FINANCIALS:
                return (
                    <AuditedFinancialsVisualisation
                        selectedMetric={selectedMetric}
                        isPDFReport={isPDFReport}
                        assesseeOrgId={assesseeOrgId}
                        companyName={companyName}
                    />
                );
            case PROVISIONAL_FINANCIALS:
                return <ProvisionalFinancials isPDFReport={isPDFReport} />;
        }
    };
    useEffect(() => {
        const financialsType = params.get('financialsType');
        if (!financialsType) return;
        // Cashflow has no mobile pill, so a mobile deep link falls back to Financial Summary; desktop is unaffected.
        const isCashflowUnavailableOnMobile = isMobile && financialsType === CASHFLOW_FROM_BANKING;
        setSelectedFinancialsType(isCashflowUnavailableOnMobile ? FINANCIAL_SUMMARY : financialsType);
    }, [params.get('financialsType'), isMobile]);

    const selectFinancialsType = (typeId: string) => {
        setSelectedFinancialsType(typeId);
        updateQueryParams(params, setParams, { financialsType: typeId }, location);
        Mixpanel.track(AICA_FINANCIAL_STATEMENTTYPE_SWITCHED, { statementType: typeId });
    };

    const selectCashflowFromBanking = () => {
        setSelectedFinancialsType(CASHFLOW_FROM_BANKING);
        updateQueryParams(params, setParams, { financialsType: CASHFLOW_FROM_BANKING }, location);
        Mixpanel.track(AICA_CASHFLOW_BANKING);
    };

    return (
        <div className={styles.Financials}>
            {!isPDFReport && (
                <div className={styles.Selector}>
                    {FINANCIALS_TYPES.map((type, i) => (
                        <div
                            key={`${type.id}-${i}`}
                            className={clsx(styles.Option, {
                                [styles.SelectedOption]: type.id === selectedFinancialsType,
                            })}
                            // Toggle buttons, not a tablist: a tablist promises arrow-key roving
                            // focus we do not implement. Same call, and same shape, as the
                            // CategorySelector pills these sit above.
                            role="button"
                            aria-pressed={type.id === selectedFinancialsType}
                            tabIndex={0}
                            onClick={() => selectFinancialsType(type.id)}
                            onKeyDown={(e) => {
                                if (e.key !== KEYBOARD_EVENTS.ENTER && e.key !== KEYBOARD_EVENTS.SPACE) return;
                                e.preventDefault();
                                selectFinancialsType(type.id);
                            }}
                        >
                            {type.label}
                        </div>
                    ))}
                    {!isMobile && (
                        <div
                            className={styles.CashflowChip}
                            role="button"
                            tabIndex={0}
                            onClick={selectCashflowFromBanking}
                            onKeyDown={(e) => {
                                if (e.key === KEYBOARD_EVENTS.ENTER || e.key === KEYBOARD_EVENTS.SPACE) {
                                    e.preventDefault();
                                    selectCashflowFromBanking();
                                }
                            }}
                        >
                            <img src={ctaPurpleRight} alt="" />
                            Cashflow From Banking
                        </div>
                    )}
                    {!isMobile && assesseeOrgId && <ReconPill orgId={assesseeOrgId} scope={RECON_SCOPE.FINANCIALS} />}
                </div>
            )}
            <div className={styles.Content} style={{ padding: isPDFReport ? '0' : undefined }}>
                {selectedFinancialsType === AUDITED_FINANCIALS && !isPDFReport && !isMobile && (
                    <AicaOverview style={{ marginBottom: '1.5rem' }} module={params.get('selectedTab') ?? AUDITED_FINANCIALS} />
                )}
                {getRenderComponent()}
            </div>
        </div>
    );
}

export default Financials;
